/**
 * Hover notes on the Qualifier column — each cell that holds qualifier symbol(s) shows what they mean
 * ("J: Estimated value…"), from the controlled dbo.Qualifiers list (Symbol · Definition, the same feed
 * as the Qualifiers picker and the in-cell dropdown). G6 addition (user request 2026-09-24); OG shows
 * the definitions only in the Qualifiers popup. Display-only — placed as Univer notes (the 'qualifier'
 * group of SdmsUniverHandle.setQcNotes), never written to the saved workbook.
 */
import { api } from '../../api/client';
import type { QcCellNote } from './sdmsQcNotes';
import type { SheetDisplay } from './sdmsQcNotes';

export interface QualifierDef { symbol: string; definition: string }

/** The separators a Qualifier cell may carry between symbols — the picker writes "B,J"; typed or
 *  imported values may use ";" or spaces after the comma. */
const SPLIT_RE = /\s*[,;]\s*/;

/**
 * Notes for every non-blank Qualifier cell of the given result sheets. `qualCol` / `fromRow` are 0-based
 * (the column and the first band row). `defs` is keyed by the UPPER-CASED symbol. A cell whose symbols
 * are all unknown gets no note; unknown symbols next to known ones are listed as "not in the list".
 */
export function buildQualifierNotes(
  sheets: ReadonlyArray<{ name: string; display: SheetDisplay }>,
  qualCol: number,
  fromRow: number,
  defs: ReadonlyMap<string, QualifierDef>,
): QcCellNote[] {
  const out: QcCellNote[] = [];
  for (const { name, display } of sheets) {
    for (let row = fromRow; row < display.length; row++) {
      let text = (display[row]?.[qualCol] ?? '').trim();
      if (!text) continue;
      // Univer's multi-select dropdown writes '["B","J"]' (normalised to "B,J" right after the pick —
      // SdmsUniverSheet qualifierPickText — but a rebuild can run before that lands).
      if (text.startsWith('[')) {
        try { const a = JSON.parse(text); if (Array.isArray(a)) text = a.map(x => String(x ?? '')).join(','); }
        catch { /* not JSON — use as typed */ }
      }
      const symbols = [...new Set(text.split(SPLIT_RE).map(s => s.trim()).filter(Boolean))];
      const lines: string[] = [];
      let known = 0;
      for (const sym of symbols) {
        const d = defs.get(sym.toUpperCase());
        if (d) { known++; lines.push(`${d.symbol}: ${d.definition.trim() || '(no definition)'}`); }
        else lines.push(`${sym}: not in the Qualifiers list`);
      }
      if (known) out.push({ sheetName: name, row, col: qualCol, text: lines.join('\n') });
    }
  }
  return out;
}

let defCache: Map<string, QualifierDef> | null = null;

/** Symbol → definition from `/api/v1/analysis/qualifiers`, cached for the session once non-empty.
 *  Returns an empty map on failure (the caller then places no notes). */
export async function fetchQualifierDefinitions(): Promise<Map<string, QualifierDef>> {
  if (defCache) return defCache;
  try {
    const res = await api.get('/api/v1/analysis/qualifiers', { silentLoading: true } as never);
    const rows = (res.data as { data?: { symbol?: string | null; definition?: string | null }[] }).data ?? [];
    const m = new Map<string, QualifierDef>();
    for (const r of rows) {
      const symbol = (r.symbol ?? '').trim();
      if (symbol && !m.has(symbol.toUpperCase())) m.set(symbol.toUpperCase(), { symbol, definition: r.definition ?? '' });
    }
    if (m.size) defCache = m;
    return m;
  } catch (e) {
    console.warn('[SDMS] qualifier definitions unavailable — no qualifier notes', e);
    return new Map();
  }
}
